import { useEffect, useState } from "react";
import useMountEffect from "core/hooks/useMountEffect";
import { useParams, matchPath } from "react-router";
import { animated, useSpring, useTransition } from "react-spring";
import {
  useIsRestoring,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import DocumentTitle from "react-document-title";
import { DateTime } from "luxon";
import styled from "styled-components";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faClose } from "@fortawesome/pro-regular-svg-icons";

import Navigate from "core/components/Navigate";
import useNavigate from "core/hooks/useNavigate";
import {
  useOrganizationByIdQuery,
  useProductCatalog,
  useUserByIdQuery,
} from "core/queries";
import { PERMISSIONS, FLAG_KEYS, catalogQueryKey } from "core/constants";
import { hasAnalyticsHubUserAccess } from "common/utilities";
import useAuthenticatedCall from "core/hooks/useAuthenticatedCall";
import useCurrentUser from "core/hooks/useCurrentUser";
import { getProductCatalogVersion } from "modules/admin/actions";
import { getLastActiveCredential } from "modules/admin/utilities";
import useHasPermissions from "core/hooks/useHasPermissions";
import { useCheckFeatureFlag } from "core/hooks/featureFlagging";
import Navigation from "core/components/Navigation";
import Loader from "core/components/Loader";

import ActiveMembershipSwitcher from "./ActiveMembershipSwitcher";
import useActiveMembership from "core/hooks/useActiveMembership";
import UnauthorizedError from "core/error/UnauthorizedError";

const BannerContainer = styled.div`
  background-color: #0097c0;
  color: #fff;
  font-family: "Inter";
  text-align: center;
  padding-top: 6px;
  padding-bottom: 5px;
  font-size: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 30px;

  ${(props) =>
    props.variant === "warning" &&
    `
    background-color: #d68712;
    color: #fff
  `}

  ${(props) =>
    props.variant === "error" &&
    `
    background-color: #BA351C;
    color: #fff;
  `}
`;

// Matt: Intentionally co-locating for now, but likely moving to core in future
const Banner = ({ isVisible, variant, onDismiss, children }) => {
  const containerHeight = useSpring({
    height: isVisible ? "30px" : "0px",
    delay: isVisible ? 675 : 0,
    position: "sticky",
    top: "0px",
    zIndex: 2000,
  });

  const transitions = useTransition([isVisible], {
    from: { position: "relative", zIndex: 2000, y: -30, width: "100%" },
    enter: [{ y: 0 }],
    leave: { position: "relative", zIndex: 2000, y: -30, width: "100%" },
    exitBeforeEnter: true,
  });

  return (
    <animated.div
      style={{ backgroundColor: "#2f2e41", height: "0px", ...containerHeight }}
    >
      {transitions((style, isVisible) => {
        return isVisible ? (
          <BannerContainer variant={variant} as={animated.div} style={style}>
            <div
              style={{
                width: "100%",
                display: "grid",
                gridTemplateColumns: "40px 1fr 40px",
              }}
            >
              <div />
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                {children}
              </div>
              {onDismiss && (
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    cursor: "pointer",
                  }}
                >
                  <FontAwesomeIcon
                    icon={faClose}
                    size="lg"
                    onClick={onDismiss}
                  />
                </div>
              )}
            </div>
          </BannerContainer>
        ) : null;
      })}
    </animated.div>
  );
};

const Header = styled.header`
  display: flex;
  width: 100%;
  height: 68px;
  padding: 14px 13px;
  align-items: center;
  gap: 10px;

  border-bottom: 1px solid var(--Neutral-60, #898d8f);
  background: #fff;
`;

let hasCheckedCatalogVersion = false;

const LoadWhileRestoringLocalStorage = ({ children }) => {
  const isRestoring = useIsRestoring();

  if (isRestoring) {
    return (
      <Loader
        style={{
          height: "100dvh",
          paddingBottom: "20vh",
        }}
      />
    );
  }

  return children;
};

const protectedRoutes = [
  { to: "/" },
  { to: "/panel/*" },
  { to: "/404" },
  { to: "/unauthorized" },
  {
    to: "/organizations",
    permissions: "organizations:read",
  },
  {
    to: "/users/*",
    permissions: "all:users:read",
  },
  {
    to: "/organizations/create",
    permissions: "organizations:create",
  },
  {
    to: "/user-profile/:userId/*",
    permissions: "user:read-profile",
  },
  {
    to: "/organization/:orgId/product/configure",
    permissions: "product-license:write",
  },
  {
    to: "/organization/:orgId/product/confirm",
    permissions: "product-license:write",
  },
  {
    to: "/organization/:orgId/product/*",
    permissions: "product-license:read",
  },
  {
    to: "/organization/:orgId/*",
    permissions: "organization:read",
  },
  {
    to: "/analytics/*",
    permissions: "analytics:read",
  },
  {
    to: "/analytics-hub",
    permissions: "analytics-hub:read",
    featureFlag: FLAG_KEYS.ANALYTICS_HUB,
  },
  {
    to: "/configurations/:orgId",
    permissions: "configurations:read",
    featureFlag: FLAG_KEYS.MANAGED_PROVIDERS,
  },
  {
    to: "/configurations/:orgId/aliases",
    permissions: "configurations:read",
    featureFlag: FLAG_KEYS.MANAGED_PROVIDERS,
  },
  {
    to: "/configurations/:orgId/relationships",
    permissions: "configurations:read",
    featureFlag: FLAG_KEYS.MANAGED_PROVIDERS,
  },
  {
    to: "/configurations/:orgId/relationships/update",
    permissions: "configurations:write",
    featureFlag: FLAG_KEYS.MANAGED_PROVIDERS,
  },
  {
    to: "/configurations/:orgId/relationships/update/:npi",
    permissions: "configurations:write",
    featureFlag: FLAG_KEYS.MANAGED_PROVIDERS,
  },
  {
    to: "/configurations/:orgId/relationships/remove",
    permissions: "configurations:write",
    featureFlag: FLAG_KEYS.MANAGED_PROVIDERS,
  },
  {
    to: "/configurations/:orgId/relationships/remove/:npi",
    permissions: "configurations:write",
    featureFlag: FLAG_KEYS.MANAGED_PROVIDERS,
  },
  {
    to: "/configurations/:orgId/aliases/update",
    permissions: "configurations:write",
    featureFlag: FLAG_KEYS.MANAGED_PROVIDERS,
  },
  {
    to: "/configurations/:orgId/aliases/update/:npi",
    permissions: "configurations:write",
    featureFlag: FLAG_KEYS.MANAGED_PROVIDERS,
  },
  {
    to: "/configurations/:orgId/aliases/remove",
    permissions: "configurations:write",
    featureFlag: FLAG_KEYS.MANAGED_PROVIDERS,
  },
  {
    to: "/configurations/:orgId/aliases/remove/:npi",
    permissions: "configurations:write",
    featureFlag: FLAG_KEYS.MANAGED_PROVIDERS,
  },
  {
    to: "/analytics-hub/*",
    permissions: "organizations:read",
    featureFlag: FLAG_KEYS.ANALYTICS_HUB,
  },
];

const AppLayout = ({ pageTitle, children }) => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { orgId: orgIdParam } = useParams();
  const { currentUser } = useCurrentUser();
  const { activeMembership } = useActiveMembership();

  useMountEffect(() => {
    try {
      navigator.storage.estimate().then((result) => {
        let usedSpace = Math.round((result.usage / 1024 / 1024) * 100) / 100;

        console.log(`Using ${usedSpace}MB of async storage.`);
      });
    } catch (error) {
      console.error(error);
    }
  });

  const { catalog: productCatalog } = useProductCatalog();

  const lastActiveCredential = getLastActiveCredential(
    activeMembership.data_access_info
  );
  const isCredentialExpiring = lastActiveCredential
    ? lastActiveCredential.days_to_expire <= 7
    : false;

  const fetchCatalogVersion = useAuthenticatedCall(getProductCatalogVersion);
  const userCanAccessCatalogVersion = useHasPermissions(
    PERMISSIONS[activeMembership.role],
    "catalog-version:read"
  );
  const { data: productCatalogVersion } = useQuery({
    queryKey: ["catalog-version"],
    queryFn: fetchCatalogVersion,
    enabled: userCanAccessCatalogVersion,
  });

  // Check if catalog version changed
  useEffect(() => {
    if (
      !hasCheckedCatalogVersion &&
      productCatalogVersion?.version &&
      productCatalog &&
      Object.keys(productCatalog).length > 0
    ) {
      hasCheckedCatalogVersion = true;

      if (
        productCatalogVersion.version !==
        productCatalog[Object.keys(productCatalog)[0]].version
      ) {
        queryClient.invalidateQueries({ queryKey: catalogQueryKey });
      }
    }
  }, [productCatalog, productCatalogVersion, queryClient]);

  const [isExpirationBannerDismmissed, setExpirationBannerDismmissed] =
    useState(() => {
      const now = DateTime.now();
      const localValue = localStorage.getItem("dateExpirationBannerDismmissed");

      if (localValue) {
        const isToday = now.hasSame(DateTime.fromISO(localValue), "day");

        return isToday;
      }

      return false;
    });

  const handleDismissExpirationBanner = () => {
    localStorage.setItem(
      "dateExpirationBannerDismmissed",
      DateTime.now().toISO()
    );
    setExpirationBannerDismmissed(true);
  };

  const matchedRoute = protectedRoutes.find((route) =>
    matchPath(route.to, location.pathname)
  );

  const hasFeatureAccess = useCheckFeatureFlag(matchedRoute.featureFlag);

  const hasAnalyticsHubAccessLevel =
    hasAnalyticsHubUserAccess(activeMembership);
  const isAhuRoute = matchedRoute?.featureFlag === FLAG_KEYS.ANALYTICS_HUB;
  const canAccessRoute =
    hasFeatureAccess && (!isAhuRoute || hasAnalyticsHubAccessLevel); // This applies the AHU feature check to only Analytics Hub routes

  const userIsAuthorized = useHasPermissions(
    PERMISSIONS[activeMembership.role] || [],
    matchedRoute.permissions,
    {
      userMembershipIds: currentUser.memberships?.map((m) => m.organization_id),
      orgIdParam,
    }
  );

  if (!userIsAuthorized) {
    return (
      <Navigate
        to="/"
        state={{ error: "You do not have permission to view that page." }}
        replace
      />
    );
  }

  return (
    <>
      <DocumentTitle
        title={pageTitle ? `Populi | ${pageTitle}` : "Populi Platform"}
      />

      <div style={{ display: "flex", height: "100dvh" }}>
        <Navigation />
        <div
          style={{
            flex: 1,
            height: "100%",
            position: "relative",
            overflowX: "auto",
            overflowY: "hidden",
          }}
        >
          {/* Data Access Credential Expiration Banner */}
          <Banner
            isVisible={isCredentialExpiring && !isExpirationBannerDismmissed}
            variant={
              lastActiveCredential?.days_to_expire <= 3 ? "error" : "warning"
            }
            onDismiss={handleDismissExpirationBanner}
          >
            <div style={{ marginRight: "5px" }}>
              {lastActiveCredential?.days_to_expire <= 0
                ? "Your Data Access Credential has expired!"
                : `Your Data Access Credential will expire in ${lastActiveCredential?.days_to_expire} days.`}
            </div>

            <div
              onClick={() =>
                navigate(`/user-profile/${currentUser.userId}/credentials`)
              }
              style={{
                textDecoration: "underline",
                cursor: "pointer",
              }}
            >
              Get New Credential
            </div>
          </Banner>

          <LoadWhileRestoringLocalStorage>
            <div
              style={{
                height: "100dvh",
                display: "flex",
                flexDirection: "column",
              }}
            >
              <Header>
                <ActiveMembershipSwitcher />
              </Header>
              <main
                style={{
                  overflow: "auto",
                  position: "relative",
                  flex: 1,
                }}
              >
                {canAccessRoute ? (
                  children
                ) : (
                  <UnauthorizedError
                    url={location.href}
                    role={activeMembership.role}
                  />
                )}
              </main>
            </div>
          </LoadWhileRestoringLocalStorage>
        </div>
      </div>
    </>
  );
};

export default AppLayout;
